import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Avatar, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import { formatLocalizedDate } from '@/lib/date-utils';
import { capitalizeName } from '@/lib/utils/name.ts';
import { CalendarCheck2, Image as ImageIcon } from 'lucide-react';

import { PresenceIndicator } from '../presence-indicator';
import HeaderButtons from './header-buttons';
import { ShippingHeaderInfos } from './shipping-policy/shipping-header-infos';

function Header() {
  const {
    deal: { id: dealId, ...deal },
  } = useDealContext();
  const { t, i18n } = useTranslation();
  const user = useUser();

  const isChoosePage = user?.isInternalUser;

  const headerTranslationOptions = { interpolation: { escapeValue: false } };
  const title = `${t('page.offer')} ${deal.hubspotInfos.dealName}`;
  const formattedHubspotStartDate = formatLocalizedDate(
    deal.hubspotInfos.startDate,
    i18n.language
  );
  const formattedCreatedAt = formatLocalizedDate(deal.createdAt, i18n.language);
  const createdBy = deal.hubspotInfos.dealManager?.name;
  const managerPhotoUrl = deal.hubspotInfos.dealManager?.photoUrl;
  const hasGoogleDriveImagesUrl = Boolean(deal.googleDriveImagesUrl?.trim());
  return (
    <header data-component="Header" className="px-10 pt-6">
      <section className="flex items-center justify-between">
        <section className="flex flex-col gap-3">
          <main className="flex items-center gap-3">
            <h1 className="whitespace-nowrap text-2xl font-bold">{title}</h1>
            {isChoosePage && (
              <TooltipProvider delayDuration={200}>
                <div className="flex items-center gap-3">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={`/deal/${dealId}/settings`}
                        className={
                          hasGoogleDriveImagesUrl
                            ? 'inline-flex shrink-0 rounded-md border border-border bg-green-50 p-1.5 text-green-700 transition-colors hover:bg-green-100 hover:text-green-800'
                            : 'inline-flex shrink-0 rounded-md border border-dashed border-muted-foreground/45 p-1.5 text-muted-foreground/75 transition-colors hover:bg-muted hover:text-foreground/90'
                        }
                        aria-label={t(
                          hasGoogleDriveImagesUrl
                            ? 'ui-v2.header.google-drive-images.tooltip_go_settings_linked'
                            : 'ui-v2.header.google-drive-images.tooltip_go_settings_empty'
                        )}
                      >
                        <ImageIcon className="h-5 w-5" aria-hidden />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {hasGoogleDriveImagesUrl
                        ? t(
                            'ui-v2.header.google-drive-images.tooltip_go_settings_linked'
                          )
                        : t(
                            'ui-v2.header.google-drive-images.tooltip_go_settings_empty'
                          )}
                    </TooltipContent>
                  </Tooltip>
                  <PresenceIndicator dealId={dealId} />
                </div>
              </TooltipProvider>
            )}
          </main>
          <aside>
            <ul className="flex items-center gap-3 text-sm text-muted-foreground">
              <li className="inline-flex items-center gap-1">
                <CalendarCheck2 className="h-4 w-4" />
                {formattedHubspotStartDate
                  ? t('page.launch_on', {
                      ...headerTranslationOptions,
                      date: formattedHubspotStartDate,
                    })
                  : t('page.created_at', {
                      ...headerTranslationOptions,
                      date: formattedCreatedAt,
                    })}
              </li>
              <li className="inline-flex items-center gap-1">
                {managerPhotoUrl && (
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={managerPhotoUrl} alt={createdBy} />
                  </Avatar>
                )}
                {capitalizeName(createdBy)}
              </li>
              <li>
                <ShippingHeaderInfos dealId={dealId} />
              </li>
            </ul>
          </aside>
        </section>
        <HeaderButtons />
      </section>
    </header>
  );
}
export default Header;
